import BillingAccountantInfoForm from "components/form/billing-client/BillingAccountantInfoForm";
import BillingClientCorporateInfoForm from "components/form/billing-client/BillingClientCorporateInfoForm";
import BillingClientPersonalInfoForm from "components/form/billing-client/BillingClientPersonalInfoForm";
import Button from "core/buttons/button/Button";
import InfoContext from "pages/InfoContext";
import _ from "lodash";
import { BILLING_COMPANY_FORM_DEFAULT_VALUES } from "constants/forms";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import { LANG, POPUP_STATUS, QUERY_STATUS } from "constants/constants";
import { formatReceivedData } from "utils/subCompanyHelper";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useApiFieldErrors } from "utils/useApiFieldErrors";
import { useUserContext } from "api/user/UserProvider";
import PropTypes from "prop-types";
import colors from "assets/styles/mixins/colors.scss";

const PersonalBillingClient = ({
  readOnly,
  setReadOnly,
  onSubmit,
  getStatus,
  getCode,
  id,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
    setError,
  } = useForm({
    defaultValues: BILLING_COMPANY_FORM_DEFAULT_VALUES,
  });
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);
  const { dataCountry, billingClientBySlug, editBillingClientStatus, lang } =
    useContext(InfoContext);
  const [cellHighlight, setCellHighlight] = useState(false);
  const [isValidated, setIsValidated] = useState(false);

  useEffect(() => {
    isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  const { setApiErrorResponse } = useUserContext();

  useApiFieldErrors(setError, setReadOnly);

  const initForm = () => {
    const defaultKeys = Object.keys(BILLING_COMPANY_FORM_DEFAULT_VALUES);
    const formattedReceivedData = formatReceivedData({
      data: billingClientBySlug,
      nationalities: dataCountry,
    });
    Object.keys(formattedReceivedData).forEach((elm) => {
      if (defaultKeys.includes(elm) && formattedReceivedData[elm]) {
        setValue(elm, formattedReceivedData[elm]);
      }
    });
    const nationalityKey =
      lang === LANG.FR ? "nationality_fr" : "nationality_eng";
    const nationality = dataCountry?.filter(
      (elm) => elm?.id === billingClientBySlug?.nationality_id
    );
    setValue("nationality_id", [
      {
        label: nationality[0]?.[nationalityKey],
        value: billingClientBySlug?.nationality_id,
      },
    ]);
  };
  useEffect(() => {
    if (!_.isEmpty(billingClientBySlug)) {
      initForm();
    }
  }, [billingClientBySlug, dataCountry, lang]);

  const onReset = () => {
    initForm();
    setReadOnly(true);
    clearErrors();
    setApiErrorResponse(null);
    window.scrollTo(0, 0);
  };
  const onError = (errors) => {
    if (!_.isEmpty(errors)) {
      getStatus(POPUP_STATUS.ERROR);
      getCode(FIELD_REQUIRED);
    }
  };

  const onGuardedSubmit = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await onSubmit(info);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <form
      id="create-billing-client-form"
      className="subcontracting-company"
      onSubmit={handleSubmit(onGuardedSubmit, onError)}
    >
      <div className="subcontracting-company-form">
        <BillingClientPersonalInfoForm
          setValue={(field, value) => {
            setValue(field, value);
            clearErrors(field);
          }}
          watch={watch}
          control={control}
          register={register}
          errors={errors}
          readOnly={readOnly}
          id={id}
          cellHighlight={cellHighlight}
        />
        <div className="bottom-billing-client-wrapper ">
          <BillingClientCorporateInfoForm
            setValue={(field, value) => {
              setValue(field, value);
              clearErrors(field);
            }}
            watch={watch}
            control={control}
            register={register}
            errors={errors}
            readOnly={readOnly}
            cellHighlight={cellHighlight}
          />
          <BillingAccountantInfoForm
            setValue={(field, value) => {
              setValue(field, value);
              clearErrors(field);
            }}
            watch={watch}
            control={control}
            register={register}
            errors={errors}
            readOnly={readOnly}
            cellHighlight={cellHighlight}
          />
        </div>
        {!readOnly && (
          <div style={{ marginTop: "50px" }} className="form-buttons">
            <Button
              className="btn-standard btn-outline"
              type="button"
              onClick={onReset}
              id="button-form-reset"
            >
              <FormattedMessage id="cancel" defaultMessage="Annuler" />
            </Button>
            <Button
              color="btn-medium gray-700"
              type="submit"
              id="button-form-validation"
              onClick={() => setIsValidated(true)}
              disabled={editBillingClientStatus === QUERY_STATUS.LOADING}
              backgroundColor={
                editBillingClientStatus === QUERY_STATUS.LOADING &&
                colors.disabledGrey
              }
            >
              <FormattedMessage
                id="button_form_validation_edition"
                defaultMessage="Valider l'édition"
              />
            </Button>
          </div>
        )}
      </div>
    </form>
  );
};

export default PersonalBillingClient;

PersonalBillingClient.propTypes = {
  readOnly: PropTypes.bool,
  setReadOnly: PropTypes.func,
  onSubmit: PropTypes.func,
  getStatus: PropTypes.func,
  getCode: PropTypes.func,
  id: PropTypes.number,
};
